import '@fountain/ripple/styles';
import '@/styles/globals.css';
import '@/styles/tailwind.css';
import 'tippy.js/dist/tippy.css';

import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { OpenAPI } from 'api-clients/monolith';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';

import { App } from '@/components/app';
import { ConfirmationProvider } from '@/components/base/confirmation';
import { ToasterProvider } from '@/components/base/toaster';
import { WxCopilotProvider } from '@/components/pages/wx-copilot-host/context';
import { AuthProvider } from '@/contexts/auth-context';
import LanguageProvider from '@/i18n/language-provider';
import { loadIntlPolyfills } from '@/i18n/messages/polyfills';
import { queryKeys } from '@/query-keys';
import { domainMeta } from '@/utils/domain-meta';
import { initSentry } from '@/utils/sentry';
import { mapEnvironment } from '@/utils/standard-event-properties';

import { PostHogRootProvider } from './contexts/posthog-context';
import { RecommendationProvider } from './contexts/recommendation-context';

// Before the first render, so a failure during bootstrap is reported.
initSentry();

OpenAPI.BASE = domainMeta.monolithOrigin;
OpenAPI.WITH_CREDENTIALS = true;

// Module scope keeps the object stable, so the root proxy is built once.
const POSTHOG_ROOT_PROPERTIES = {
  product: 'hire',
  ui: 'source',
  environment: mapEnvironment(),
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      // No need to retry 404s. Other calls should retry once, not more.
      retry: (failureCount, error): boolean => {
        if (failureCount >= 1) return false;
        return !error.message?.includes('Not Found');
      },
    },
  },
});
// The Post step reads the Direct Apply preview after Review Job unmounts its
// observer; keep the entry until Post so the order carries the questionnaire.
queryClient.setQueryDefaults(queryKeys.jobPosting.questionnairePreviewRoot, {
  gcTime: Infinity,
});

void loadIntlPolyfills()
  .catch((error: unknown) => Sentry.captureException(error))
  .then(() => {
    ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
      <React.StrictMode>
        {/*
      Page-level boundaries handle the errors they expect. This one is the last
      resort: a render error above them already blanks the dashboard out,
      because React unmounts the tree it cannot recover. No `fallback`, so the
      screen looks the same and the error is reported instead of lost.
    */}
        <Sentry.ErrorBoundary>
          <BrowserRouter basename={domainMeta.basename}>
            <PostHogRootProvider properties={POSTHOG_ROOT_PROPERTIES}>
              <QueryClientProvider client={queryClient}>
                <AuthProvider>
                  <LanguageProvider>
                    <ToasterProvider>
                      <ConfirmationProvider>
                        <WxCopilotProvider>
                          <RecommendationProvider>
                            <App />
                          </RecommendationProvider>
                        </WxCopilotProvider>
                      </ConfirmationProvider>
                    </ToasterProvider>
                  </LanguageProvider>
                </AuthProvider>
              </QueryClientProvider>
            </PostHogRootProvider>
          </BrowserRouter>
        </Sentry.ErrorBoundary>
      </React.StrictMode>,
    );
  });
